import axiosInstance from "./axios";

// gets a presigned url from s3
export const getPresign = async (file, token) => {
  try {
    const { data } = await axiosInstance.post(
      "/uploads/presign/",
      {
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        size: file.size,
      },
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );
    return data;
  } catch (err) {
    console.error("Failed to get presigned url", err);
    throw err;
  }
};

// handles the final upload to s3 bucket
export const finalizeUpload = async ({ key, file }, token) => {
  try {
    const { data } = await axiosInstance.post(
      "/uploads/finalize/",
      {
        key,
        original_filename: file.name,
        mime: file.type,
      },
      {
        headers: {
          Authorization: `Token ${token}`,
        },
      }
    );
    return data; // e.g., { id, status: "pending" }
  } catch (err) {
    throw err;
  }
};

// takes a presigned url and uploads to s3 storage
export function uploadToS3({ upload_url, headers, file, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", upload_url);
    xhr.setRequestHeader("x-amz-acl", "private");
    xhr.setRequestHeader("x-amz-server-side-encryption", "AES256");

    // Set only the same Content-Type that was used to presign
    if (headers?.["Content-Type"]) {
      xhr.setRequestHeader("Content-Type", headers["Content-Type"]);
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      reject(new Error(`S3 upload failed with status ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Network error while uploading to S3"));
    xhr.send(file);
  });
}

export const pollUntilReady = async (
  { id, intervalMs = 2000, timeoutMs = 5 * 60 * 1000 },
  token
) => {
  const start = Date.now();
  while (true) {
    const { data } = await axiosInstance.get(`/posts/preview/${id}/?_=${Date.now()}`, {
      headers: { Authorization: `Token ${token}` },
    }); // cache-bust
    if (data.status === "done") {
      return data;
    }
    if (data.status === "error") throw new Error(data.detail || "Processing failed");
    if (Date.now() - start > timeoutMs) throw new Error("Timed out waiting for processing");
    await new Promise((r) => setTimeout(r, intervalMs));
  }
};

// creates a post on the user's profile
export const createUserPost = async (formData, token) => {
  try {
    const { data } = await axiosInstance.post(`/posts/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Token ${token}`,
      },
    });
    return data;
  } catch (err) {
    console.error("Failed to create user post", err);
    throw err;
  }
};

// creates a post on thread
export const createThreadPost = async (threadId, formData, token) => {
  try {
    const { data } = await axiosInstance.post(`/threads/${threadId}/posts/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Token ${token}`,
      },
    });
    return data;
  } catch (err) {
    console.error("Error posting to thread", err);
    throw err;
  }
};

// gets saved status of a post <id> for logged in user
export const fetchSavedStatus = async (postId, token) => {
  try {
    const { data } = await axiosInstance.get(`/saved/status/`, {
      params: { post: postId },
      headers: { Authorization: `Token ${token}` },
    });
    return data;
  } catch (err) {
    console.error("Error getting saved status");
    throw err;
  }
};

// toggles saved status of a post
export const toggleSave = async (postId, token) => {
  try {
    const { data } = await axiosInstance.post(
      `/saved/toggle/`,
      { post: postId },
      { headers: { Authorization: `Token ${token}` } }
    );
    return data;
  } catch (err) {
    console.error("Error setting saved status");
    throw err;
  }
};

export const createPostComment = async (postId, text, token, parentId = "") => {
  try {
    const payload = { text };
    if (parentId != null && parentId !== "") {
      payload.parent = parentId;
    }
    const { data } = await axiosInstance.post(`/posts/${postId}/comments/`, payload, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });
    return data;
  } catch (err) {
    console.error("Error posting comment", err);
    throw err;
  }
};

export const deletePost = async (postId) => {
  try {
    const { data } = await axiosInstance.delete(`/posts/${postId}/`);
    return data;
  } catch (err) {
    console.error("Error deleting post", err);
  }
};
